/**
 * Status-color tokens — one hue per semantic (ui-audit P1-4, systemic fix #3).
 *
 * Rules:
 * - One hue per semantic. Never reuse crimson `#ba2156` for a status fill;
 *   it is reserved for brand/primary (buttons, rail, breadcrumbs).
 * - Legibility follows the badge recipe: dark text on light fills,
 *   white text only on dark fills.
 * - Statuses not listed fall back to DRAFT (neutral gray) rather than red,
 *   so unknown states never read as "error".
 */

export const STATUS_PILL_CLASSES = {
  /** Neutral / awaiting first transition */
  DRAFT: "bg-gray-400 text-gray-900",
  /** In-flight order workflow */
  ORDER: "bg-blue-400 text-gray-900",
  /** Success / completed / paid / enabled */
  SUCCESS: "bg-green-400 text-gray-900",
  /** In-progress attention (e.g. awaiting payment) */
  WARNING: "bg-amber-400 text-gray-900",
  /** Failure / cancelled / disabled */
  DANGER: "bg-red-600 text-white",
} as const;

export type StatusSemantic = keyof typeof STATUS_PILL_CLASSES;

const ORDER_STATUS_SEMANTIC: Record<string, StatusSemantic> = {
  DRAFT: "DRAFT",
  CREATE: "ORDER",
  ORDER: "ORDER",
  PAYMENT: "WARNING",
  PAYMENT_COMPLETED: "SUCCESS",
  DONE: "SUCCESS",
  CANCEL: "DANGER",
  TIMEOUT: "DANGER",
  FAILED: "DANGER",
};

const ORDER_STATUS_LABELS: Record<string, string> = {
  DRAFT: "Draft",
  PAYMENT: "Awaiting Payment",
  PAYMENT_COMPLETED: "Payment Completed",
  ORDER: "Processing",
  DONE: "Completed",
  TIMEOUT: "Timed Out",
  FAILED: "Failed",
  CANCEL: "Cancelled",
  CREATE: "Processing",
};

/** Tailwind pill classes for an order status (falls back to neutral DRAFT). */
export const orderStatusPillClass = (status?: string): string =>
  STATUS_PILL_CLASSES[ORDER_STATUS_SEMANTIC[status ?? ""] ?? "DRAFT"];

/** Human-readable label for an order status (unknown → raw value, empty → "Draft"). */
export const orderStatusLabel = (
  status?: string,
  t?: (key: string, options?: { defaultValue?: string }) => string
): string => {
  const key = status || "DRAFT";
  const fallback = status ? ORDER_STATUS_LABELS[status] ?? status : "Draft";
  return t ? t(`order.status.${key}`, { defaultValue: fallback }) : fallback;
};
